import { useState, useEffect } from 'react'
import cx from 'classnames'
import { Skeleton } from 'antd'
import { AlgoliaArticle } from 'types'
import { LeftSideBar } from 'components/Modal/LeftSideBar'
import { ReactComponent as DocumentIcon } from 'assets/icons/document16.svg'
import { Tooltip } from 'common/Tooltip/Tooltip'
import { ReactComponent as CollapseIcon } from 'assets/icons/triangle16.svg'
import { ReactComponent as TemplatesIcon } from 'assets/icons/templates16.svg'

import Styles from './CreateFromTemplateSidePanel.module.scss'
import templatesStore from 'stores/TemplatesStore'
import { observer } from 'mobx-react-lite'
import { Button } from 'components/Button'
import { CORE_PATH } from 'paths'
import { REACT_APP_FRONTEND_URL } from 'env'
import { useSuperAdmin } from 'hooks/useSuperAdmin'
import useTemplateCategories from './hooks/useTemplateCategories'

interface CreateFromTemplateSidePanelProps {
  categories: ReturnType<typeof useTemplateCategories>['data']
  loading: string
  setSelectedArticleId: Function
  selectedArticleId: string | null
}

interface CollapsibleCategoryProps {
  category: any
  setSelectedArticleId: Function
  selectedArticleId: string | null
  isCollapsed: boolean
  onCollapse: Function
}

const CreateFromTemplateSidePanel = observer(
  ({ categories, loading, setSelectedArticleId, selectedArticleId }: CreateFromTemplateSidePanelProps) => {
    const { exitSuperAdminMode, superAdminMode } = useSuperAdmin()
    const defaultCategory =
      categories?.find((x) => x.name.toLowerCase().includes('popular'))?.name || categories?.[0]?.name

    const [collapsed, setCollapsed] = useState<string | undefined>(templatesStore.selectedCategory || defaultCategory)

    const { selectedCategory } = templatesStore

    useEffect(() => {
      setCollapsed(selectedCategory || defaultCategory)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory, categories])

    const navigateToCore = () => {
      if (superAdminMode) exitSuperAdminMode()

      templatesStore.setState({ isTemplateModalOpen: false })

      window.open(`${REACT_APP_FRONTEND_URL}${CORE_PATH}`)
    }
    return (
      <LeftSideBar closePanel={() => {}} showPanel={true}>
        <>
          {loading === 'loading' && (
            <>
              <Skeleton paragraph={{ rows: 20 }} className={cx(Styles.categoryPlaceholder)} active />
            </>
          )}
          {loading === 'success' && (
            <ul className={Styles.listWrapper}>
              {categories?.map((category, i) => (
                <CollapisbleCategory
                  key={category.name}
                  category={category}
                  isCollapsed={collapsed !== category.name}
                  selectedArticleId={selectedArticleId}
                  setSelectedArticleId={setSelectedArticleId}
                  onCollapse={() => {
                    collapsed === category.name ? setCollapsed('') : setCollapsed(category.name)
                    if (collapsed && collapsed !== category.name) {
                      templatesStore.setState({
                        selectedModalTemplate: category.articles[0].key,
                        selectedCategory: category.name,
                      })
                    }
                  }}
                />
              ))}
            </ul>
          )}
          <Button variant="secondary" onPress={navigateToCore} className={Styles.browseButton}>
            <TemplatesIcon />
            Browse More Templates
          </Button>
        </>
      </LeftSideBar>
    )
  },
)

const CollapisbleCategory = ({
  category,
  isCollapsed,
  selectedArticleId,
  setSelectedArticleId,
  onCollapse,
}: CollapsibleCategoryProps) => {
  const [selectedCategory, setSelectedCategory] = useState<boolean>(false)
  const collapsed = isCollapsed

  const handleClick = () => {
    onCollapse(category.name)
  }

  useEffect(
    () => setSelectedCategory(category.articles.map((a: AlgoliaArticle) => a.key).includes(selectedArticleId)),
    [selectedArticleId, category],
  )

  return (
    <li className={Styles.categoryItem}>
      <button
        className={cx(!collapsed && selectedCategory && Styles.selected, Styles.categoryTitle)}
        onClick={handleClick}
      >
        <span className={Styles.collapsing}>
          <CollapseIcon className={cx(Styles.collapseIcon, collapsed ? Styles.closed : Styles.open)} />
        </span>
        <span>{category.name}</span>
      </button>
      {!collapsed && (
        <ul>
          {category.articles.map((article: AlgoliaArticle) => (
            <li className={Styles.templateItem} key={`${category.name}-${article.key}`}>
              <button
                className={cx(selectedArticleId === article.key && Styles.selected, Styles.title)}
                onClick={() => setSelectedArticleId(article.key)}
              >
                <div className={Styles.titleWrapper}>
                  {article.header_emoji ? article.header_emoji : <DocumentIcon className={Styles.placeholderIcon} />}
                  <Tooltip label={article.title} placement="right">
                    <span className={Styles.titleSpan}>{article.title}</span>
                  </Tooltip>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

export default CreateFromTemplateSidePanel
